import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import onsiteproLogo from "../../assets/logo2 2.png";
import "./horizonTable.css";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { ensureFormUuid } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

const DEFAULT_INSPECTION_ROWS = [
  { no: 1, item: "Site Security and Public interfaces" },
  { no: 2, item: "Traffic Management / Pedestrian Segregation" },
  { no: 3, item: "Excavations (barricades / access/ support)" },
  { no: 4, item: "Work at height (scaffolds, platforms, ladders)" },
  { no: 5, item: "Fall Protection / Fall Arrest" },
  { no: 6, item: "Lifting Operations / Accessories / Certs" },
  { no: 8, item: "Exclusion zones around lifting / work at height" },
  { no: 9, item: "Edge protection / floor openings" },
  { no: 10, item: "Protection of buried / overhead services" },
  { no: 11, item: "Site Access & Egress (Walkways / Stairs / Scaffold etc.)" },
  { no: 12, item: "Work on / over or adjacent to water" },
  { no: 13, item: "Storage / Lay-down Area" },
  { no: 14, item: "Hand Tools" },
  { no: 15, item: "Hand / Arm Vibration" },
  { no: 16, item: "Lighting (Site / Task)" },
  { no: 17, item: "Welding / Burning" },
  { no: 18, item: "Material Handling (manual / assisted)" },
  { no: 19, item: "Site Tidiness / Cable Management" },
  { no: 20, item: "Fire Precautions" },
  { no: 21, item: "Electrical Equipment / Systems" },
  { no: 22, item: "Hazardous Materials / COSHH" },
  { no: 23, item: "Flammables and LPG" },
  { no: 24, item: "Mobile plant (incl. certificates)" },
  { no: 25, item: "First Aid Facilities" },
  { no: 26, item: "Welfare Facilities" },
  { no: 27, item: "Personal Protective Equipment" },
  { no: 28, item: "F10 / Safety Signs and posters" },
  { no: 29, item: "Adequate Contractor supervision" },
  { no: 30, item: "Method Statements / Risk Assessments / Permits to Work / Lifting Plans" },
  { no: 31, item: "Permissions / Licenses / Consents" },
  { no: 32, item: "Briefings of Method Statements / Risk Assessments to Operatives" },
  { no: 33, item: "Colleague Wellbeing (Fatigue/Fitness to Work)" },
  { no: 34, item: "Induction records / training records / trade certification cards" },
  { no: 35, item: "Accident reporting / investigation" },
  { no: 36, item: "Waste management" },
  { no: 37, item: "Water and Effluent Discharge" },
  { no: 38, item: "Ecology and Biodiversity" },
  { no: 39, item: "Transport, Plant and Equipment" },
  { no: 40, item: "Emissions to Air" },
  { no: 41, item: "Nuisance" }
];

const ACTIONS_NA_DISPLAY = "-";

function parseStoredDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const day = parseInt(dmy[1], 10);
    const month = parseInt(dmy[2], 10) - 1;
    const year = parseInt(dmy[3], 10);
    const d = new Date(year, month, day);
    return isNaN(d.getTime()) ? null : d;
  }
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const year = parseInt(ymd[1], 10);
    const month = parseInt(ymd[2], 10) - 1;
    const day = parseInt(ymd[3], 10);
    const d = new Date(year, month, day);
    return isNaN(d.getTime()) ? null : d;
  }
  const t = new Date(s);
  return isNaN(t.getTime()) ? null : t;
}

function dateToYmd(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatCellDate(value) {
  const d = parseStoredDate(value);
  if (!d) return value ? String(value) : "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function parseInspectionItems(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

function ensureInspectionItems(raw) {
  const list = parseInspectionItems(raw);
  return DEFAULT_INSPECTION_ROWS.map((def) => {
    const row = list.find((r) => Number(r.no) === def.no);
    if (!row) {
      return { no: def.no, item: def.item, suitable: "Y", actions: "No", comments: "" };
    }
    const suitable = row.suitable != null ? String(row.suitable) : "Y";
    let actions = row.actions != null ? String(row.actions) : "No";
    if (suitable === "NA") {
      actions = ACTIONS_NA_DISPLAY;
    } else if (actions === ACTIONS_NA_DISPLAY || !["Yes", "No"].includes(actions)) {
      actions = "No";
    }
    return {
      no: def.no,
      item: def.item,
      suitable,
      actions,
      comments: typeof row.comments === "string" ? row.comments : ""
    };
  });
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read image file"));
    reader.readAsDataURL(file);
  });
}

function normalizeImageSource(value, fallbackMime = "image/jpeg") {
  if (!value) return "";

  let src = value;
  if (Array.isArray(src)) src = src[0];
  if (src == null) return "";

  if (typeof src !== "string") src = String(src);
  src = src.trim();
  if (!src) return "";

  if (src.startsWith("[")) {
    try {
      const parsed = JSON.parse(src);
      if (Array.isArray(parsed) && parsed.length) {
        src = String(parsed[0] || "").trim();
      }
    } catch {
      const firstQuoted = src.match(/^\[\s*["']([^"']+)["']/);
      if (firstQuoted?.[1]) {
        src = firstQuoted[1].trim();
      }
    }
  }

  if ((src.startsWith('"') && src.endsWith('"')) || (src.startsWith("'") && src.endsWith("'"))) {
    src = src.slice(1, -1).trim();
  }
  if (src.startsWith('["')) src = src.slice(2);
  if (src.startsWith("['")) src = src.slice(2);
  if (src.endsWith('"]')) src = src.slice(0, -2);
  if (src.endsWith("']")) src = src.slice(0, -2);
  if ((src.startsWith("[") && src.endsWith("]"))) {
    src = src.slice(1, -1).trim();
    if ((src.startsWith('"') && src.endsWith('"')) || (src.startsWith("'") && src.endsWith("'"))) {
      src = src.slice(1, -1).trim();
    }
  }

  if (!src) return "";
  if (src.startsWith("data:image")) return src;
  if (/^(https?:)?\/\//i.test(src)) return src;

  src = src.replace(/\\\//g, "/").replace(/\\n/g, "").replace(/\\r/g, "");
  const compact = src.replace(/\s+/g, "").replace(/,+$/, "");
  if (!compact) return "";
  let mime = fallbackMime;
  if (compact.startsWith("/9j")) mime = "image/jpeg";
  else if (compact.startsWith("iVBOR")) mime = "image/png";
  else if (compact.startsWith("R0lGOD")) mime = "image/gif";
  else if (compact.startsWith("UklGR")) mime = "image/webp";
  return `data:${mime};base64,${compact}`;
}

function normalizePhotos(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((v) => normalizeImageSource(v)).filter(Boolean);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return normalizePhotos(parsed);
      } catch {}
    }
    const single = normalizeImageSource(trimmed);
    return single ? [single] : [];
  }
  return [];
}

function getPdfImageType(imageSrc, fallbackType = "JPEG") {
  if (!imageSrc || typeof imageSrc !== "string") return fallbackType;
  let b64 = imageSrc;
  const idx = imageSrc.indexOf("base64,");
  if (idx >= 0) b64 = imageSrc.slice(idx + 7);
  b64 = b64.replace(/\s/g, "");
  if (b64.startsWith("/9j")) return "JPEG";
  if (b64.startsWith("iVBOR")) return "PNG";
  if (b64.startsWith("UklGR")) return "WEBP";
  const m = imageSrc.match(/^data:image\/([a-zA-Z0-9+.-]+);base64,/i);
  if (m?.[1]) {
    const subtype = m[1].toLowerCase();
    if (subtype === "png") return "PNG";
    if (subtype === "jpg" || subtype === "jpeg") return "JPEG";
    if (subtype === "webp") return "WEBP";
  }
  return fallbackType;
}

function HseqSiteInspectionPage() {
  const canEditDelete = canEditDeleteRecords();
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [photoInputResetKey, setPhotoInputResetKey] = useState(0);

  useEffect(() => {
    fetchRecords();
    fetchAppUsers();
  }, []);

  async function fetchRecords() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/hseq-site-inspection", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setRecords(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const filtered = records.filter((r) => {
    const fields = [
      r.contract,
      r.location,
      r.teamOnSite,
      r.completedBy,
      r.date,
      r.briefDescription
    ].map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page - 1) * pageSize, page * pageSize);

  const handleEdit = (r) => {
    setEditForm({
      ...r,
      formUuid: ensureFormUuid(r?.formUuid),
      inspectionItems: ensureInspectionItems(r.inspectionItems),
      photos: normalizePhotos(r.photos)
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const body = {
        userId: editForm.userId,
        contract: editForm.contract,
        location: editForm.location,
        teamOnSite: editForm.teamOnSite,
        completedBy: editForm.completedBy,
        date: editForm.date,
        time: editForm.time,
        inspectionItems: editForm.inspectionItems,
        briefDescription: editForm.briefDescription,
        photos: normalizePhotos(editForm.photos),
        inspectorName: editForm.inspectorName,
        inspectorDate: editForm.inspectorDate,
        inspectorSignature: editForm.inspectorSignature,
        workforceConsultation: editForm.workforceConsultation,
        lastTbtProvided: editForm.lastTbtProvided,
        auditor: editForm.auditor,
        auditorDate: editForm.auditorDate,
        formUuid: ensureFormUuid(editForm.formUuid)
      };
      const res = await fetch(getBaseUrl() + "/api/hseq-site-inspection/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchRecords();
        setShowEditModal(false);
        setEditForm(null);
        toast.success("SF-015-B HSEQ Site Inspection (2) updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/hseq-site-inspection/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecords(records.filter((f) => f._id !== deleteId));
        toast.success("Record deleted!");
      } else toast.error(data.message || "Failed to delete");
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    return isNaN(x.getTime()) ? String(d) : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const getUserName = (record) => {
    const uid = record.userId;
    const u = appUsers.find((x) => x._id === uid);
    if (u) {
      const full = `${u.firstName || ""} ${u.lastName || ""}`.trim();
      return full || uid || "—";
    }
    return uid || "—";
  };

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint
      ? {
          uploadContext: options.uploadContext || "",
          onSuccess: options.onSuccess,
          onError: options.onError
        }
      : null;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const primaryColor = [139, 22, 52];

    const drawRoundedRect = (x, y, width, height, radius, fillColor, strokeColor, hasShadow = false) => {
      if (hasShadow) {
        doc.setFillColor(0, 0, 0, 0.1);
        doc.roundedRect(x + 1, y + 1, width, height, radius, radius, "F");
      }
      doc.setFillColor(...fillColor);
      doc.setDrawColor(...strokeColor);
      doc.setLineWidth(0.5);
      doc.roundedRect(x, y, width, height, radius, radius, "FD");
    };

    const headerHeight = 30;
    drawRoundedRect(0, 0, pageWidth, headerHeight, 0, primaryColor, primaryColor);

    const addLogoToPDF = () => {
      return new Promise((resolve) => {
        const logoImg = new Image();
        logoImg.crossOrigin = "anonymous";
        logoImg.onload = function () {
          try {
            const logoWidth = 70;
            const aspectRatio = logoImg.height / logoImg.width;
            const logoHeight = logoWidth * aspectRatio;
            const logoX = 10;
            const logoY = (headerHeight - logoHeight) / 2;
            const canvas = document.createElement("canvas");
            const scale = 2.5;
            canvas.width = logoImg.width * scale;
            canvas.height = logoImg.height * scale;
            const ctx = canvas.getContext("2d");
            ctx.scale(scale, scale);
            ctx.drawImage(logoImg, 0, 0);
            const highResDataUrl = canvas.toDataURL("image/png", 1.0);
            doc.addImage(highResDataUrl, "PNG", logoX, logoY, logoWidth, logoHeight, "", "NONE");
          } catch {
            doc.setFillColor(255, 255, 255);
            doc.rect(10, 6, 60, 15, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text("TAYLOR TECHNOLOGY", 15, 12);
            doc.text("SYSTEMS LIMITED", 15, 17);
          }
          resolve();
        };
        logoImg.onerror = function () {
          doc.setFillColor(255, 255, 255);
          doc.rect(10, 6, 60, 15, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
          doc.text("TAYLOR TECHNOLOGY", 15, 12);
          doc.text("SYSTEMS LIMITED", 15, 17);
          resolve();
        };
        logoImg.src = onsiteproLogo;
      });
    };

    await addLogoToPDF();

    const titleX = 90;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text("SF-015-B HSEQ Site Inspection (2)", titleX, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", titleX, 19);

    let yPosition = headerHeight + 6;
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("HSQE Site Inspection Form", pageWidth / 2, yPosition + 6, { align: "center" });
    yPosition += 14;
    const formattedDate = formatCellDate(record.date);
    const formattedInspectorDate = formatCellDate(record.inspectorDate);
    const formattedAuditorDate = formatCellDate(record.auditorDate);
    const addedBy =
      record.addedByFirstName || record.addedByLastName
        ? `${record.addedByFirstName || ""} ${record.addedByLastName || ""}`.trim()
        : getUserName(record);

    const innerTableW = pageWidth - 20;

    const tableCommon = {
      styles: {
        font: "helvetica",
        fontSize: 9,
        cellPadding: 3,
        overflow: "linebreak",
        valign: "middle"
      },
      theme: "grid",
      tableWidth: innerTableW,
      margin: { left: 10, right: 10, bottom: 30 },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: [0, 0, 0],
        lineWidth: 0.2
      },
      bodyStyles: {
        lineWidth: 0.2,
        textColor: [0, 0, 0]
      }
    };

    doc.setFillColor(139, 22, 52);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.rect(10, yPosition, pageWidth - 20, 8, "F");
    doc.text("1", 14, yPosition + 5.5);
    doc.text("Basic Information", 22, yPosition + 5.5);
    yPosition += 8;

    autoTable(doc, {
      ...tableCommon,
      startY: yPosition,
      columnStyles: {
        0: { cellWidth: 48 },
        1: { cellWidth: 47 },
        2: { cellWidth: 48 },
        3: { cellWidth: 47 }
      },
      head: [
        [
          { content: "Contract:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.contract || "", styles: { halign: "center" } },
          { content: "Location:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
          { content: record.location || "", styles: { halign: "center" } }
        ]
      ],
      body: [
        [
          { content: "Team on site:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.teamOnSite || "", styles: { halign: "center" } },
          { content: "Completed by:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
          { content: record.completedBy || "", styles: { halign: "center" } }
        ],
        [
          { content: "Date:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: formattedDate, styles: { halign: "center" } },
          { content: "Time:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
          { content: record.time || "", styles: { halign: "center" } }
        ],
        [
          { content: "Added by:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: addedBy, styles: { halign: "center" } },
          { content: "", styles: { fillColor: [230, 230, 230] } },
          { content: "", styles: {} }
        ]
      ]
    });
    yPosition = doc.lastAutoTable.finalY + 2;

    const inspectionItems = ensureInspectionItems(record.inspectionItems);
    if (yPosition + 50 > pageHeight - 30) {
      doc.addPage();
      yPosition = 20;
    }
    doc.setFillColor(139, 22, 52);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.rect(10, yPosition, pageWidth - 20, 8, "F");
    doc.text("2", 14, yPosition + 5.5);
    doc.text("Inspection Items", 22, yPosition + 5.5);
    yPosition += 8;

    const insCol0 = 10;
    const insCol2 = 22;
    const insCol3 = 22;
    const insCol4 = 32;
    const insCol1 = innerTableW - insCol0 - insCol2 - insCol3 - insCol4;

    autoTable(doc, {
      ...tableCommon,
      startY: yPosition,
      head: [
        [
          { content: "No", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
          { content: "Inspected Item", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } },
          { content: "Suitable Y/N/NA", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
          { content: "Actions Yes/No", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
          { content: "Comments", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } }
        ]
      ],
      body: inspectionItems.map((row) => {
        const act = row.actions || "";
        const actionsCell =
          act === ACTIONS_NA_DISPLAY
            ? { content: act, styles: { halign: "center", fontSize: 10, fontStyle: "bold", textColor: [139, 22, 52] } }
            : { content: act, styles: { halign: "center" } };
        return [
          { content: String(row.no), styles: { halign: "center" } },
          { content: row.item || "", styles: { halign: "left" } },
          { content: row.suitable || "", styles: { halign: "center" } },
          actionsCell,
          { content: row.comments || "", styles: { halign: "left" } }
        ];
      }),
      columnStyles: {
        0: { cellWidth: insCol0 },
        1: { cellWidth: insCol1 },
        2: { cellWidth: insCol2 },
        3: { cellWidth: insCol3 },
        4: { cellWidth: insCol4 }
      },
      headStyles: {
        ...tableCommon.headStyles,
        fontSize: 7,
        cellPadding: 3
      },
      bodyStyles: {
        ...tableCommon.bodyStyles,
        fontSize: 7,
        cellPadding: 3
      },
      styles: { ...tableCommon.styles, fontSize: 7, cellPadding: 3 }
    });
    yPosition = doc.lastAutoTable.finalY + 2;

    if (yPosition + 40 > pageHeight - 30) {
      doc.addPage();
      yPosition = 20;
    }
    doc.setFillColor(139, 22, 52);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.rect(10, yPosition, pageWidth - 20, 8, "F");
    doc.text("3", 14, yPosition + 5.5);
    doc.text("Brief Description of Works Inspected", 22, yPosition + 5.5);
    yPosition += 8;
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const brief = record.briefDescription || "—";
    const briefLines = doc.splitTextToSize(brief, pageWidth - 24);
    doc.text(briefLines, 12, yPosition + 6);
    yPosition += 6 + briefLines.length * 4.8;

    if (yPosition + 45 > pageHeight - 30) {
      doc.addPage();
      yPosition = 20;
    }
    doc.setFillColor(139, 22, 52);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.rect(10, yPosition, pageWidth - 20, 8, "F");
    doc.text("4", 14, yPosition + 5.5);
    doc.text("Workforce consultation & Last TBT", 22, yPosition + 5.5);
    yPosition += 8;
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const wf = record.workforceConsultation || "—";
    const wfLines = doc.splitTextToSize(`Workforce consultation: ${wf}`, pageWidth - 24);
    doc.text(wfLines, 12, yPosition + 6);
    yPosition += 6 + wfLines.length * 4.8;
    doc.text(`Last TBT provided: ${record.lastTbtProvided || "—"}`, 12, yPosition + 4);
    yPosition += 12;

    if (yPosition + 105 > pageHeight - 30) {
      doc.addPage();
      yPosition = 20;
    }
    doc.setFillColor(139, 22, 52);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.rect(10, yPosition, pageWidth - 20, 8, "F");
    doc.text("5", 14, yPosition + 5.5);
    doc.text("Inspector sign-off", 22, yPosition + 5.5);
    yPosition += 8;
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    autoTable(doc, {
      ...tableCommon,
      startY: yPosition,
      columnStyles: {
        0: { cellWidth: 48 },
        1: { cellWidth: 47 },
        2: { cellWidth: 48 },
        3: { cellWidth: 47 }
      },
      head: [
        [
          { content: "Inspector Name:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.inspectorName || "—", styles: { halign: "center" } },
          { content: "Inspector Date:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
          { content: formattedInspectorDate || "—", styles: { halign: "center" } }
        ]
      ],
      body: []
    });
    yPosition = doc.lastAutoTable.finalY + 3;

    const blockW = (pageWidth - 30) / 2;
    const blockH = 34;

    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(248, 248, 248);
    doc.roundedRect(10, yPosition, blockW, blockH, 3, 3, "FD");
    doc.roundedRect(20 + blockW, yPosition, blockW, blockH, 3, 3, "FD");

    doc.setTextColor(139, 22, 52);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Inspector Signature", 14, yPosition + 6.5);
    doc.text("Photos", 24 + blockW, yPosition + 6.5);

    const signatureSrc = normalizeImageSource(record.inspectorSignature, "image/png");
    if (signatureSrc) {
      try {
        const signatureType = getPdfImageType(signatureSrc, "PNG");
        doc.addImage(signatureSrc, signatureType, 14, yPosition + 9, blockW - 8, blockH - 12, "", "NONE");
      } catch {}
    } else {
      doc.setFont("helvetica", "normal");
      doc.setTextColor(120, 120, 120);
      doc.text("—", 14, yPosition + 19);
    }

    const photoList = normalizePhotos(record.photos);
    const photoSrc = photoList[0] || "";
    if (photoSrc && photoList.length === 1) {
      try {
        const photoType = getPdfImageType(photoSrc, "JPEG");
        doc.addImage(photoSrc, photoType, 24 + blockW, yPosition + 9, blockW - 8, blockH - 12, "", "NONE");
      } catch {}
    } else if (photoList.length > 1) {
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80, 80, 80);
      doc.setFontSize(8);
      doc.text(`${photoList.length} photos below`, 24 + blockW + 4, yPosition + 18);
    } else {
      doc.setFont("helvetica", "normal");
      doc.setTextColor(120, 120, 120);
      doc.text("—", 24 + blockW, yPosition + 19);
    }
    yPosition += blockH + 8;

    if (photoList.length > 1) {
      const cols = 2;
      const gap = 6;
      const marginX = 12;
      const contentW = pageWidth - 24;
      const cellW = (contentW - (cols - 1) * gap) / cols;
      const cellH = 52;
      let sectionY = yPosition;

      if (sectionY + 20 > pageHeight - 30) {
        doc.addPage();
        sectionY = 20;
      }
      doc.setTextColor(139, 22, 52);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text(`Photos (${photoList.length})`, marginX, sectionY + 5);
      sectionY += 10;

      let rowStartY = sectionY;
      for (let i = 0; i < photoList.length; i += 1) {
        const col = i % cols;
        if (col === 0 && i > 0) {
          rowStartY += cellH + gap;
        }
        if (rowStartY + cellH > pageHeight - 30) {
          doc.addPage();
          rowStartY = 20;
        }
        const x = marginX + col * (cellW + gap);
        doc.setDrawColor(220, 220, 220);
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(x, rowStartY, cellW, cellH, 2, 2, "FD");
        try {
          const photoType = getPdfImageType(photoList[i], "JPEG");
          doc.addImage(photoList[i], photoType, x + 2, rowStartY + 2, cellW - 4, cellH - 4, "", "NONE");
        } catch {}
      }
      yPosition = rowStartY + cellH + 10;
    }

    if (yPosition + 35 > pageHeight - 30) {
      doc.addPage();
      yPosition = 20;
    }
    doc.setFillColor(139, 22, 52);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.rect(10, yPosition, pageWidth - 20, 8, "F");
    doc.text("6", 14, yPosition + 5.5);
    doc.text("Auditor sign-off", 22, yPosition + 5.5);
    yPosition += 8;
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const auditorName = record.auditor || "—";
    const auditorLines = doc.splitTextToSize(`Auditor: ${auditorName}`, pageWidth - 24);
    doc.text(auditorLines, 12, yPosition + 6);
    yPosition += 6 + auditorLines.length * 4.8;
    doc.text(`Date: ${formattedAuditorDate || "—"}`, 12, yPosition + 4);
    yPosition += 12;

    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      const footerY = pageHeight - 25;
      drawRoundedRect(0, footerY, pageWidth, 25, 0, [248, 249, 250], [200, 200, 200]);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `SF-015-B HSEQ Site Inspection (2) — Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
        20,
        footerY + 8
      );
      doc.text(`Page ${p} of ${totalPages}`, pageWidth - 35, footerY + 15);
    }

    const safeContract = String(record.contract || "HSEQ-Site").replace(/[\\/:"*?<>|]+/g, "-");
    const safeDate = String(formattedDate || "inspection").replace(/[\\/:"*?<>|]+/g, "-");
    let fileName;
    if (shouldUploadToSharePoint && uploadConfig) {
      fileName = buildSharePointFileName([
        addedByName(record),
        record.location,
        toDdMmYyyy(record.date),
      ]);
    } else {
      fileName = `SF015B_Issue2_${safeContract}_${safeDate}.pdf`;
    }

    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${record.location || ""}|${record.date || ""}`;
        setUploadingKey(key);
        const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
        const dataUri = doc.output("datauristring");
        const uploadParams = {
          fileName,
          fileBase64: dataUri,
          uploadContext: uploadConfig.uploadContext, uploadSubFolder: "SF-015-B HSEQ Site Inspection"
        };
        const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: JSON.stringify(uploadParams)
        });
        if (res.status !== 204 && !res.ok) {
          let msg = "SharePoint upload failed";
          try {
            const out = await res.json();
            msg = out?.message || msg;
          } catch {
            /* ignore */
          }
          throw new Error(msg);
        }
        toast.success("PDF uploaded to SharePoint successfully!");
        if (uploadConfig.onSuccess) uploadConfig.onSuccess();
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
        if (uploadConfig.onError) uploadConfig.onError(err);
      } finally {
        setUploadingKey(null);
      }
    } else {
      doc.save(fileName);
      toast.success("SF-015-B HSEQ Site Inspection (2) PDF generated successfully!");
    }
  };

  const updateInspectionRow = (index, patch) => {
    setEditForm((prev) => {
      if (!prev) return prev;
      const next = [...(prev.inspectionItems || [])];
      const merged = { ...next[index], ...patch };
      if (merged.suitable === "NA") {
        merged.actions = ACTIONS_NA_DISPLAY;
      } else {
        if (merged.actions === ACTIONS_NA_DISPLAY || !["Yes", "No"].includes(merged.actions)) {
          merged.actions = "No";
        }
      }
      next[index] = merged;
      return { ...prev, inspectionItems: next };
    });
  };

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const imageFiles = files.filter((f) => f.type.startsWith("image/"));
    if (!imageFiles.length) {
      toast.error("Please select image files.");
      setPhotoInputResetKey((k) => k + 1);
      return;
    }
    try {
      const dataUrls = await Promise.all(imageFiles.map((file) => fileToDataUrl(file)));
      const valid = dataUrls.filter(Boolean);
      if (!valid.length) {
        toast.error("Failed to load images.");
        return;
      }
      setEditForm((prev) =>
        prev
          ? {
              ...prev,
              photos: [...normalizePhotos(prev.photos), ...valid]
            }
          : prev
      );
    } catch {
      toast.error("Failed to load images.");
    } finally {
      setPhotoInputResetKey((k) => k + 1);
    }
  };

  const removePhotoAtIndex = (index) => {
    setEditForm((prev) => {
      if (!prev) return prev;
      const next = normalizePhotos(prev.photos).filter((_, idx) => idx !== index);
      return { ...prev, photos: next };
    });
  };

  const renderSection = (title, children) => (
    <div style={{ marginBottom: 20, padding: 16, border: "1px solid #e2e8f0", borderRadius: 12, background: "#f8fafc" }}>
      <div style={{ fontWeight: 700, color: THEME, marginBottom: 12, fontSize: 13 }}>{title}</div>
      {children}
    </div>
  );

  const tableCellStyle = { border: "1px solid #d1d5db", padding: "6px 8px", fontSize: 11, verticalAlign: "middle" };
  const tableHeaderStyle = { ...tableCellStyle, fontWeight: 600, color: THEME, background: "#fafafa" };

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-015-B HSEQ Site Inspection (2)</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
            <input
              type="text"
              placeholder="Type to Search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              style={{
                padding: "10px 12px 10px 36px",
                borderRadius: 12,
                border: "1px solid #e2e8f0",
                fontSize: 14,
                outline: "none",
                width: 250,
                boxSizing: "border-box"
              }}
              onFocus={(e) => (e.target.style.borderColor = THEME)}
              onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
            />
          </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Contract</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Location</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Team on site</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Time</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Completed by</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />
                    Loading records...
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>
                    No records found.
                  </td>
                </tr>
              ) : (
                paged.map((r, idx) => {
                  const updatedDate = r.updatedAt || r.createdAt;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.contract || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.location || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.teamOnSite || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatCellDate(r.date)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.time || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.completedBy || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button type="button" onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => generatePDF(r)}
                            title="Generate SF-015-B (2) PDF"
                            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}
                          >
                            <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} />
                          </button>
                          {(() => {
                            const k = `${r._id || ""}|${r.location || ""}|${r.date || ""}`;
                            const busy = uploadingKey === k;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!busy) {
                                    generatePDF(r, {
                                      uploadOnly: true,
                                      uploadContext: "hseq-forms-tablets"
                                    });
                                  }
                                }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  cursor: busy ? "not-allowed" : "pointer",
                                  padding: 6,
                                  opacity: busy ? 0.6 : 1
                                }}
                              >
                                <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                              </button>
                            );
                          })()}
                          {canEditDelete && (
                            <button type="button" onClick={() => handleDelete(r._id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button
            type="button"
            className="horizon-table-btn"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: page === 1 ? "#f7fafc" : "#fff",
              color: page === 1 ? "#a0aec0" : THEME,
              cursor: page === 1 ? "not-allowed" : "pointer"
            }}
          >
            Prev
          </button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className="horizon-table-btn"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: page === totalPages ? "#f7fafc" : "#fff",
              color: page === totalPages ? "#a0aec0" : THEME,
              cursor: page === totalPages ? "not-allowed" : "pointer"
            }}
          >
            Next
          </button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 1200, margin: "0 auto" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff" }}>
                <h5 className="modal-title">Edit SF-015-B HSEQ Site Inspection (2)</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                {renderSection("contract · location · teamOnSite · completedBy · date · time", (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>contract</label>
                      <input type="text" className="form-control form-control-sm" value={editForm.contract || ""} onChange={(e) => setEditForm({ ...editForm, contract: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>teamOnSite</label>
                      <input type="text" className="form-control form-control-sm" value={editForm.teamOnSite || ""} onChange={(e) => setEditForm({ ...editForm, teamOnSite: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>location</label>
                      <input type="text" className="form-control form-control-sm" value={editForm.location || ""} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>completedBy</label>
                      <input type="text" className="form-control form-control-sm" value={editForm.completedBy || ""} onChange={(e) => setEditForm({ ...editForm, completedBy: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>date</label>
                      <DatePicker
                        selected={parseStoredDate(editForm.date)}
                        onChange={(d) => setEditForm({ ...editForm, date: d ? dateToYmd(d) : "" })}
                        dateFormat="dd-MM-yyyy"
                        className="form-control form-control-sm"
                        style={{ width: "100%", fontSize: 12, borderColor: THEME, borderRadius: 8 }}
                        placeholderText="Select date"
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>time</label>
                      <input type="text" className="form-control form-control-sm" value={editForm.time || ""} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} />
                    </div>
                  </div>
                ))}
                {renderSection("Inspection Items (Issue 2)", (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #d1d5db", borderRadius: 8 }}>
                      <thead>
                        <tr>
                          <th style={tableHeaderStyle}>No</th>
                          <th style={{ ...tableHeaderStyle, minWidth: 220 }}>Inspected Item</th>
                          <th style={tableHeaderStyle}>Suitable Y / N / NA</th>
                          <th style={tableHeaderStyle}>Actions Yes / No</th>
                          <th style={{ ...tableHeaderStyle, minWidth: 160 }}>Comments</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(editForm.inspectionItems || []).map((row, i) => (
                          <tr key={row.no}>
                            <td style={tableCellStyle}>{row.no}</td>
                            <td style={tableCellStyle}>{row.item}</td>
                            <td style={tableCellStyle}>
                              <select className="form-select form-select-sm" value={row.suitable} onChange={(e) => updateInspectionRow(i, { suitable: e.target.value })} style={{ fontSize: 11 }}>
                                <option value="Y">Y</option>
                                <option value="N">N</option>
                                <option value="NA">NA</option>
                              </select>
                            </td>
                            <td style={{ ...tableCellStyle, textAlign: "center", verticalAlign: "middle" }}>
                              {row.suitable === "NA" ? (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minHeight: 31,
                                    minWidth: 44,
                                    fontSize: 20,
                                    fontWeight: 700,
                                    color: THEME,
                                    lineHeight: 1,
                                  }}
                                >
                                  {ACTIONS_NA_DISPLAY}
                                </span>
                              ) : (
                                <select className="form-select form-select-sm" value={row.actions} onChange={(e) => updateInspectionRow(i, { actions: e.target.value })} style={{ fontSize: 11 }}>
                                  <option value="Yes">Yes</option>
                                  <option value="No">No</option>
                                </select>
                              )}
                            </td>
                            <td style={tableCellStyle}>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={row.comments || ""}
                                onChange={(e) => updateInspectionRow(i, { comments: e.target.value })}
                                style={{ fontSize: 11, minWidth: 140 }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
                <div style={{ marginBottom: 20, padding: 16, border: "1px solid #e2e8f0", borderRadius: 12, background: "#fff" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div style={{ fontWeight: 700, color: THEME, fontSize: 13 }}>Photos</div>
                    <span style={{ background: "#f1f5f9", color: "#334155", border: "1px solid #e2e8f0", borderRadius: 999, padding: "4px 10px", fontSize: 12, fontWeight: 700 }}>
                      {normalizePhotos(editForm.photos).length} photo{normalizePhotos(editForm.photos).length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <label style={{ fontSize: 12, color: THEME, fontWeight: 600, display: "block", marginBottom: 8 }}>Upload images</label>
                  <input
                    key={photoInputResetKey}
                    id="hseq-photo-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoSelect}
                    className="form-control form-control-sm"
                    style={{ borderColor: THEME, borderRadius: 8, marginBottom: 12 }}
                  />
                  <div style={{ border: "1px dashed #cbd5e1", borderRadius: 8, minHeight: 120, background: "#f8fafc", padding: 12 }}>
                    {normalizePhotos(editForm.photos).length ? (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12 }}>
                        {normalizePhotos(editForm.photos).map((img, idx) => (
                          <div
                            key={`${idx}-${String(img).slice(0, 24)}`}
                            style={{ position: "relative", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", overflow: "hidden" }}
                          >
                            <img
                              src={normalizeImageSource(img)}
                              alt={`Inspection ${idx + 1}`}
                              style={{ width: "100%", height: 120, objectFit: "cover", display: "block" }}
                            />
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 8px", borderTop: "1px solid #f1f5f9" }}>
                              <span style={{ fontSize: 11, fontWeight: 600, color: "#475569" }}>Image {idx + 1}</span>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                style={{ padding: "2px 8px", fontSize: 11 }}
                                onClick={() => removePhotoAtIndex(idx)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ minHeight: 100, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#94a3b8", gap: 6 }}>
                        <i className="fa fa-image" style={{ fontSize: 22 }} />
                        <span style={{ fontSize: 12 }}>No photos uploaded yet</span>
                      </div>
                    )}
                  </div>
                  {normalizePhotos(editForm.photos).length > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      style={{ marginTop: 10 }}
                      onClick={() => setEditForm({ ...editForm, photos: [] })}
                    >
                      Remove all photos
                    </button>
                  )}
                </div>
                {renderSection("briefDescription", (
                  <textarea
                    className="form-control form-control-sm"
                    rows={4}
                    value={editForm.briefDescription || ""}
                    onChange={(e) => setEditForm({ ...editForm, briefDescription: e.target.value })}
                    style={{ borderColor: THEME, borderRadius: 8, fontSize: 12 }}
                  />
                ))}
                <div className="row" style={{ marginBottom: 20 }}>
                  <div className="col-md-6">
                    <div style={{ padding: 16, border: "1px solid #e2e8f0", borderRadius: 12, background: "#f8fafc", height: "100%" }}>
                      <div style={{ fontWeight: 700, color: THEME, marginBottom: 10, fontSize: 13 }}>workforceConsultation</div>
                      <textarea
                        className="form-control form-control-sm"
                        rows={6}
                        value={editForm.workforceConsultation || ""}
                        onChange={(e) => setEditForm({ ...editForm, workforceConsultation: e.target.value })}
                        style={{ borderColor: THEME, borderRadius: 8 }}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div style={{ padding: 16, border: "1px solid #e2e8f0", borderRadius: 12, background: "#f8fafc", height: "100%" }}>
                      <div style={{ fontWeight: 700, color: THEME, marginBottom: 10, fontSize: 13 }}>lastTbtProvided</div>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editForm.lastTbtProvided || ""}
                        onChange={(e) => setEditForm({ ...editForm, lastTbtProvided: e.target.value })}
                        style={{ borderColor: THEME, borderRadius: 8 }}
                      />
                    </div>
                  </div>
                </div>
                {renderSection("auditor · auditorDate", (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>auditor</label>
                      <input type="text" className="form-control form-control-sm" value={editForm.auditor || ""} onChange={(e) => setEditForm({ ...editForm, auditor: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>auditorDate</label>
                      <DatePicker
                        selected={parseStoredDate(editForm.auditorDate)}
                        onChange={(d) => setEditForm({ ...editForm, auditorDate: d ? dateToYmd(d) : "" })}
                        dateFormat="dd-MM-yyyy"
                        className="form-control form-control-sm"
                        style={{ width: "100%", fontSize: 12, borderColor: THEME, borderRadius: 8 }}
                        placeholderText="Select date"
                      />
                    </div>
                  </div>
                ))}
                <div style={{ marginBottom: 20, padding: 16, border: "1px solid #e2e8f0", borderRadius: 12, background: "#fff" }}>
                  <div style={{ fontWeight: 700, color: THEME, marginBottom: 10, fontSize: 13 }}>Inspector Sign-off</div>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Name"
                    value={editForm.inspectorName || ""}
                    onChange={(e) => setEditForm({ ...editForm, inspectorName: e.target.value })}
                    style={{ borderColor: THEME, borderRadius: 12, marginBottom: 12, height: 38 }}
                  />
                  <DatePicker
                    selected={parseStoredDate(editForm.inspectorDate)}
                    onChange={(d) => setEditForm({ ...editForm, inspectorDate: d ? dateToYmd(d) : "" })}
                    dateFormat="dd-MM-yyyy"
                    className="form-control form-control-sm"
                    style={{ width: "100%", borderColor: THEME, borderRadius: 12, marginBottom: 12, height: 38 }}
                    placeholderText="Date"
                  />
                  <div className="row">
                    <div className="col-md-6">
                      <div style={{ fontSize: 12, fontWeight: 700, color: THEME, marginBottom: 6 }}>Signed:</div>
                      <div
                        style={{
                          width: "100%",
                          border: "2px solid #8b1634",
                          borderRadius: "18px",
                          background: "#e9cfd6",
                          padding: "0.5rem",
                          textAlign: "center",
                          position: "relative",
                          minHeight: "90px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          pointerEvents: "none"
                        }}
                      >
                        {editForm.inspectorSignature ? (
                          <img
                            src={
                              normalizeImageSource(editForm.inspectorSignature, "image/png")
                            }
                            alt="Inspector Signature"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "70px",
                              objectFit: "contain",
                              border: "1px solid #ddd",
                              borderRadius: "8px",
                              padding: "4px",
                              backgroundColor: "white",
                              opacity: 1,
                              filter: "contrast(1.1) brightness(1)"
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <div style={{ color: "#888", fontSize: "14px", padding: "16px" }}>
                            Signature (Disabled in Edit)
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600 }} onClick={handleEditSave}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-015-B HSEQ Site Inspection (2) File"
        importPath="hseq-site-inspection/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setRecords((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-015-B HSEQ Site Inspection (2)</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this record?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default HseqSiteInspectionPage;
